/**
 * Prompts for the Linear Dev Flow template (see `../linear-dev-flow.ts`).
 *
 * The flow is status-driven: a human moves a Linear issue into "Ready to
 * Implement", which kicks off a plan -> human approval -> implement -> agent
 * self-review loop -> ready-for-review pipeline. Each stage is a separate
 * coding agent running on a devbox with the target repo checked out and the
 * `gh` CLI available. The agents own the GitHub PR lifecycle (open draft,
 * push fixes, mark ready) because there are no GitHub PR flow actions — the
 * flow only orchestrates the high-level lifecycle and Linear status writes.
 *
 * These prompts use only plain `{{path}}` substitution by design. Optional
 * fields are referenced directly; an unknown or absent path resolves to an
 * empty string, so optional sections render blank when their value is missing.
 * Memory is appended by the engine at launch when `useMemory` is set, so the
 * prompts do not reference `{{memory.relevant}}` themselves.
 */

/**
 * Plan stage. Verifies the ticket against the codebase and produces an
 * implementation plan for a human to review. Does NOT write code.
 */
export const LINEAR_DEV_PLAN_PROMPT = `## Linear Issue

**Identifier:** {{trigger.issueIdentifier}}
**Title:** {{trigger.issueTitle}}
**Link:** {{trigger.issueUrl}}

## Description

{{trigger.issueDescription}}

## Your Task

You are scoping this ticket before any code is written. Working in the attached
repository:

1. Verify the ticket is actionable: confirm the described problem or request is
   real and locate the relevant code. Note anything ambiguous or missing.
2. Produce a concrete implementation plan: the approach, the specific files and
   functions you expect to change, the tests you will add or update, and the
   main risks.

Do NOT implement anything yet — this plan goes to a human for approval first.

## Output

You MUST respond with JSON matching this schema:
- "plan": the implementation plan as Markdown (approach, files, tests, risks)
- "summary": a one or two sentence readiness assessment for the reviewer
  (call out blockers or open questions if the ticket is not cleanly actionable)`;

/**
 * Implement stage. Runs after a human approves the plan. Implements the change
 * and opens a DRAFT pull request.
 */
export const LINEAR_DEV_IMPLEMENT_PROMPT = `## Linear Issue

**Identifier:** {{trigger.issueIdentifier}}
**Title:** {{trigger.issueTitle}}
**Link:** {{trigger.issueUrl}}

## Approved Plan

{{nodes.plan.output.plan}}

## Reviewer Notes on the Plan

{{nodes.plan-review.approvalResponse.feedback}}

## Your Task

Implement the change in the attached repository, following the approved plan and
incorporating any reviewer notes above.

1. Make a focused, minimal change that fully addresses the issue. Add or update
   tests. Ensure the build and existing tests pass.
2. Commit your work to a new branch and open a **draft** pull request
   (\`gh pr create --draft\`):
   - Title: "{{trigger.issueIdentifier}}: {{trigger.issueTitle}}"
   - Body: a short description of the change and a link to {{trigger.issueUrl}}.

Leave the PR as a draft — it will be self-reviewed before it is marked ready.

## Output

You MUST respond with JSON matching this schema:
- "status": "pr_opened" if you opened a draft PR, or "something_went_wrong" if you could not
- "pr": the URL of the pull request (required when status is "pr_opened")
- "summary": a brief description of what you implemented, or an explanation of what went wrong`;

/**
 * Review stage. A fresh agent critically reviews the open draft PR. When the
 * code clears the bar it marks the PR ready for review; otherwise it returns
 * actionable feedback and the flow loops back to the revise stage.
 */
export const LINEAR_DEV_REVIEW_PROMPT = `## Linear Issue

**Identifier:** {{trigger.issueIdentifier}}
**Title:** {{trigger.issueTitle}}

## Pull Request Under Review

{{nodes.implement.output.pr}}

## Your Task

You are an independent, critical code reviewer. Check out the pull request branch
(\`gh pr checkout\`) and review the diff against the issue:

- Correctness: does it actually solve the issue, with no regressions?
- Tests: is the change covered by meaningful tests that pass?
- Quality: clear, scoped, idiomatic, no leftover debug or dead code.

If the change clears the bar, mark the PR ready for review (\`gh pr ready\`) and
return verdict "approved". If it does not, return verdict "changes_requested"
with specific, actionable feedback. Do NOT merge the PR.

If you genuinely cannot review the PR (for example you cannot check it out, or it
does not exist), return verdict "something_went_wrong" with feedback explaining
what blocked you.

## Output

You MUST respond with JSON matching this schema:
- "verdict": "approved" if the PR is ready for human review, "changes_requested" if it needs more work, or "something_went_wrong" if you could not review it
- "feedback": specific, actionable review feedback (what must change, and why); when "something_went_wrong", explain what blocked the review
- "pr": the URL of the pull request you reviewed`;

/**
 * Revise stage. Addresses the reviewer's feedback on the same PR branch, then
 * the flow loops back to the review stage.
 */
export const LINEAR_DEV_REVISE_PROMPT = `## Linear Issue

**Identifier:** {{trigger.issueIdentifier}}
**Title:** {{trigger.issueTitle}}

## Pull Request

{{nodes.implement.output.pr}}

## Review Feedback to Address

{{nodes.review.output.feedback}}

## Your Task

Address every point in the review feedback above on the existing pull request
branch (\`gh pr checkout\`). Push your updates to the same branch and keep the PR
a draft — it will be reviewed again. Ensure the build and tests still pass.

## Output

You MUST respond with JSON matching this schema:
- "status": "revised" if you pushed updates, or "something_went_wrong" if you could not
- "summary": a brief description of how you addressed the feedback`;
